import { useEffect, useState } from "react";
import { api, fmtEur } from "../../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  AreaChart, Area, PieChart, Pie, Cell, Legend, Line, ComposedChart,
} from "recharts";

const COLORS = ["#0033A0", "#FFCC00", "#34C759", "#FF3B30", "#9333EA", "#0EA5E9", "#F59E0B", "#10B981"];

function CardWrap({ title, sub, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid={testid}>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{title}</div>
          {sub && <div className="text-[10px] text-[var(--ag-text-muted)] mt-0.5">{sub}</div>}
        </div>
      </div>
      <div className="mt-3" style={{ height: 220 }}>
        <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
      </div>
    </div>
  );
}

export function HistoryCharts() {
  const [history, setHistory] = useState([]);
  const [breakdown, setBreakdown] = useState({ top_wash_types: [], services_by_operario: [] });

  useEffect(() => {
    (async () => {
      const [h, b] = await Promise.all([
        api.get("/dashboard/history?months=6"),
        api.get("/dashboard/breakdown"),
      ]);
      setHistory(h.data.months || []);
      setBreakdown(b.data || {});
    })();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* A. Ingresos mensuales + beneficio neto */}
      <CardWrap title="Ingresos mensuales · 6 meses" sub="Ingresos (barras) y Beneficio neto (línea)" testid="chart-history">
        <ComposedChart data={history}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" fontSize={10} stroke="#6B7280" />
          <YAxis fontSize={10} stroke="#6B7280" tickFormatter={(v)=> v >= 1000 ? `${Math.round(v/1000)}k` : v} />
          <Tooltip formatter={(v)=> fmtEur(v)} />
          <Bar dataKey="ingresos" fill="#0033A0" radius={[2,2,0,0]} />
          <Line type="monotone" dataKey="beneficio_neto" stroke="#34C759" strokeWidth={2} dot={{ r: 3 }} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </ComposedChart>
      </CardWrap>

      {/* B. Hucha de Impuestos acumulada (IVA repercutido vs soportado + retenciones) */}
      <CardWrap title="Hucha de Impuestos · acumulado mensual" sub="Dinero apartado para Hacienda" testid="chart-hucha">
        <AreaChart data={history}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="label" fontSize={10} stroke="#6B7280" />
          <YAxis fontSize={10} stroke="#6B7280" tickFormatter={(v)=> v >= 1000 ? `${Math.round(v/1000)}k` : v} />
          <Tooltip formatter={(v)=> fmtEur(v)} />
          <Area type="monotone" dataKey="iva_repercutido" stackId="1" stroke="#0033A0" fill="#0033A0" fillOpacity={0.35} name="IVA repercutido" />
          <Area type="monotone" dataKey="retenciones" stackId="1" stroke="#FFCC00" fill="#FFCC00" fillOpacity={0.55} name="Retenciones" />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </AreaChart>
      </CardWrap>

      {/* C. Top tipos de servicio */}
      <CardWrap title="Top servicios del mes" sub="Categorías más demandadas" testid="chart-top-types">
        <BarChart data={breakdown.top_wash_types} layout="vertical" margin={{ left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis type="number" fontSize={10} stroke="#6B7280" />
          <YAxis type="category" dataKey="name" fontSize={10} width={150} stroke="#6B7280" />
          <Tooltip />
          <Bar dataKey="count" fill="#111827" radius={[0,2,2,0]} />
        </BarChart>
      </CardWrap>

      {/* D. Servicios por colaborador */}
      <CardWrap title="Reparto por operario · mes" sub="Servicios realizados por persona" testid="chart-by-op">
        <PieChart>
          <Pie data={breakdown.services_by_operario} dataKey="count" nameKey="name" cx="50%" cy="50%"
               innerRadius={50} outerRadius={80} paddingAngle={2}>
            {breakdown.services_by_operario.map((op, i)=> <Cell key={`op-${op.name || i}`} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </CardWrap>
    </div>
  );
}
